import { useQuery } from "@tanstack/react-query";
import {
  BarChart3,
  Boxes,
  Building2,
  CalendarDays,
  ClipboardList,
  Contact,
  CreditCard,
  Gauge,
  Handshake,
  Inbox,
  Mail,
  Menu,
  Monitor,
  Moon,
  Plug,
  Settings,
  ShieldCheck,
  Sun,
  Ticket,
  Truck,
  X,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { api } from "../api/client";
import type { Workspace } from "../api/types";
import { useAuth } from "../auth/AuthProvider";
import {
  authorityLabel,
  membershipAllows,
  membershipAllowsIn,
  type Permission,
} from "../auth/permissions";
import { BrandLockup } from "../brand/BrandMark";
import { initials, titleCase } from "../lib/format";
import { PRODUCT_NAME } from "../product";
import { useSaveAppearance } from "../theme/accountAppearance";
import { THEMES, type Theme, useTheme } from "../theme/ThemeProvider";
import type { TenantBrand } from "../theme/tenantBrand";
import { Notice } from "../ui/components";
import { RouteErrorBoundary } from "../ui/ErrorBoundary";
import { ScopeMenu } from "./ScopeMenu";
import { useVenueScope } from "./venueScope";
import { sectionRootAfterVenueChange } from "./venueSections";
import { useWorkspace } from "./workspace";
import { useWorkspaceAccess } from "./workspaceAccess";
import { workspaceNames } from "./workspaceLens";

interface TenantIdentity {
  displayName: string;
  brand: TenantBrand;
}

interface NavigationItem {
  to: string;
  label: string;
  icon: typeof Gauge;
  permission?: Permission;
  operator?: boolean;
  // Which half of the building this belongs to. Left out for the destinations that answer for
  // both, either because they are the organisation itself or because they cover the two sides in
  // one place, and those are offered whichever workspace is open.
  workspace?: Workspace;
  group: "Work" | "Relationships" | "Operations" | "Manage";
}

const navigation: NavigationItem[] = [
  // Home and the diary answer for whichever half is open rather than belonging to one, so neither
  // carries a workspace. Everything that does carry one leaves the list when the other side is.
  { to: "/", label: "Home", icon: Gauge, permission: "calendar.view", group: "Work" },
  {
    to: "/events",
    label: "Events",
    icon: ClipboardList,
    permission: "calendar.view",
    workspace: "events",
    group: "Work",
  },
  // Two entries because they are two jobs. Events are what this organisation is running;
  // bookings are the space let to somebody else when it is not running one.
  {
    to: "/bookings",
    label: "Bookings",
    icon: Ticket,
    permission: "calendar.view",
    workspace: "bookings",
    group: "Work",
  },
  {
    to: "/calendar",
    label: "Calendar",
    icon: CalendarDays,
    permission: "calendar.view",
    group: "Work",
  },
  {
    to: "/portal-inbox",
    label: "Customer requests",
    icon: Inbox,
    permission: "bookings.manage",
    workspace: "bookings",
    group: "Work",
  },
  // The address book is kept by the lettings side, because a contact is somebody the client lets
  // to. Naming one on an event is done from the event itself, so the events side reaches contacts
  // through the work rather than through this list.
  {
    to: "/contacts",
    label: "Contacts",
    icon: Contact,
    permission: "customers.manage",
    workspace: "bookings",
    group: "Relationships",
  },
  // Talking to a customer is relationship work, so the mailbox sits with the people it is
  // about rather than under settings with the mail driver that carries it. It serves both halves:
  // the organisation writes to whoever it is dealing with, whichever side that dealing is on.
  {
    to: "/emails",
    label: "Emails",
    icon: Mail,
    permission: "emails.send",
    group: "Relationships",
  },
  // Sponsorship is the client finding a backer for their own season. A third party who hires the
  // hall and brings a sponsor is managing that themselves, so this stays with the client's events.
  {
    to: "/sponsors",
    label: "Sponsors",
    icon: Handshake,
    permission: "sponsors.manage",
    workspace: "events",
    group: "Relationships",
  },
  // Suppliers sit with the people the organisation deals with rather than with inventory: an
  // inventory item is kit the client owns and counts back into a store, and a vendor is somebody
  // they have a relationship with and pay. Hiring one in commits the client's own money to their
  // own programme, which is why it belongs to events.
  {
    to: "/vendors",
    label: "Vendors",
    icon: Truck,
    permission: "vendors.manage",
    workspace: "events",
    group: "Relationships",
  },
  {
    to: "/venues",
    label: "Venues",
    icon: Building2,
    permission: "venues.manage",
    group: "Operations",
  },
  {
    to: "/inventory",
    label: "Inventory",
    icon: Boxes,
    permission: "inventory.operate",
    group: "Operations",
  },
  {
    to: "/finance",
    label: "Finance",
    icon: CreditCard,
    permission: "invoices.issue",
    group: "Operations",
  },
  {
    to: "/analytics",
    label: "Analytics",
    icon: BarChart3,
    permission: "analytics.view",
    group: "Operations",
  },
  {
    to: "/integrations",
    label: "Integrations",
    icon: Plug,
    permission: "integrations.manage",
    group: "Manage",
  },
  {
    to: "/settings",
    label: "Settings",
    icon: Settings,
    permission: "calendar.view",
    group: "Manage",
  },
  {
    to: "/operator/overview",
    label: "Platform admin",
    icon: ShieldCheck,
    operator: true,
    group: "Manage",
  },
];

// The three appearance choices as icons rather than a dropdown: the glyphs platforms already
// use for light, dark and follow-the-device, in the corner the dropdowns used to crowd.
const themeIcons: Record<Theme, typeof Sun> = {
  light: Sun,
  dark: Moon,
  system: Monitor,
};

// The switch between the two halves of the building, in the place the workspace name used to sit.
// Somebody holding a role on one side only has nothing to switch to, and is shown the name of the
// half they are in rather than a control with a single option, which would suggest there is more
// of the product waiting for them than they have been given.
function WorkspaceBadge() {
  const auth = useAuth();
  const { workspace, available, setWorkspace } = useWorkspace();
  if (!auth.session?.selectedMembership) return <small>Platform workspace</small>;
  if (available.length < 2) return <small>{workspaceNames[workspace]}</small>;
  return (
    <ScopeMenu
      label="Workspace"
      value={workspace}
      options={available.map((option) => ({ value: option, label: workspaceNames[option] }))}
      onChange={(value) => setWorkspace(value as Workspace)}
    />
  );
}

// The venue a person is scoping their view to. Every screen outside Finance and Analytics
// shows one venue at a time, so the switch always names a real venue; somebody assigned to
// a single venue sees the name of it and nothing to change, which is the same one-option
// treatment as the workspace switch.
//
// A record open in the old venue is not the new venue's, so changing venue inside a booking, an
// event or a conversation returns to the root of that section, where the newly chosen venue's
// list is. Which pages that applies to is decided in venueSections.ts.
function VenueScopeBadge() {
  const auth = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const { selectedVenueId, venues, showSelector, label, setVenueId } = useVenueScope();
  if (!auth.session?.selectedMembership) return <small>Platform administration</small>;
  if (!showSelector) return <small>{label}</small>;
  const changeVenue = (venueId: string) => {
    if (venueId === selectedVenueId) return;
    setVenueId(venueId);
    const root = sectionRootAfterVenueChange(location.pathname);
    if (root) navigate(root);
  };
  return (
    <ScopeMenu
      label="Venue scope"
      className="topbar-scope"
      value={selectedVenueId}
      options={venues.map((venue) => ({ value: venue.id, label: venue.name }))}
      onChange={changeVenue}
    />
  );
}

/**
 * Says what a frozen workspace is, and how long is left of it.
 *
 * The refusing happens in two places that cannot be forgotten: requireEntitlement on the server, and
 * the guard in api/client.ts on the way out of the browser. Neither of those can explain itself in
 * advance, which is what this is for. A person who only ever met the refusals would learn what they
 * could not do one attempt at a time, and never why, or for how much longer.
 */
function AccessBanner() {
  const access = useWorkspaceAccess();
  if (!access) return null;
  if (access.readOnly) {
    const days = access.readOnlyDaysRemaining;
    return (
      <Notice tone="danger" title="This workspace is read-only">
        <span>
          The subscription is no longer active, so everything can be read and nothing can be
          changed.
          {days !== null && days > 0
            ? ` Your data stays here for ${days} more ${days === 1 ? "day" : "days"}, which is time to reactivate or to export what you need.`
            : ""}{" "}
          <Link to="/settings/billing">Reactivate the subscription</Link> to open it again exactly
          as you left it.
        </span>
      </Notice>
    );
  }
  if (access.limited) {
    return (
      <Notice tone="warning" title="This workspace is limited">
        <span>
          A subscription payment has not gone through. Work already booked keeps running, and
          nothing new can be set up until it is settled.{" "}
          <Link to="/settings/billing">Update billing</Link> to lift this.
        </span>
      </Notice>
    );
  }
  return null;
}

export function AppShell() {
  const auth = useAuth();
  const theme = useTheme();
  const saveAppearance = useSaveAppearance();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  const columnRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLElement>(null);
  const shownPath = useRef(location.pathname);
  // The frame beside the navigation is the scroller (see .main-column in styles.css), so a new
  // page has to start at its top rather than wherever the last one was left, and focus moves to
  // the page area so that keyboard scrolling and a screen reader's reading both begin with the
  // page that was asked for rather than with the link that asked for it. Only on a change of
  // page: the first render leaves focus wherever the sign-in form or the browser put it.
  useEffect(() => {
    if (shownPath.current === location.pathname) return;
    shownPath.current = location.pathname;
    if (columnRef.current) columnRef.current.scrollTop = 0;
    contentRef.current?.focus({ preventScroll: true });
  }, [location.pathname]);
  const membership = auth.session?.selectedMembership;
  const tenant = useQuery({
    queryKey: ["tenant", membership?.tenantId],
    queryFn: () => api<TenantIdentity>("/api/v1/tenant"),
    enabled: Boolean(membership),
  });
  const tenantName = tenant.data?.displayName ?? membership?.tenantName ?? PRODUCT_NAME;

  const platformRole = auth.session?.user.platformRole ?? "none";
  const { workspace } = useWorkspace();
  const visible = navigation.filter((item) => {
    if (item.operator) return platformRole !== "none";
    if (!membership || !item.permission) return false;
    // A destination belonging to one half is offered only while that half is open, and only to
    // somebody who holds a role there. The rest are judged across both roles, because standing in
    // the organisation is not narrowed by which side of it you happen to be looking at.
    if (item.workspace) {
      return (
        item.workspace === workspace &&
        membershipAllowsIn(membership, item.workspace, item.permission)
      );
    }
    return membershipAllows(membership, item.permission);
  });

  // A failed write leaves the choice applied to this browser, which is exactly what this control did
  // before an account could hold one: nothing is lost, and nothing is claimed either, because this
  // strip announces nothing when it succeeds. The profile page is where a save is confirmed and
  // where a failure is reported, since a page has somewhere to say so and a footer does not.
  function changeAppearance(next: { theme?: Theme }) {
    saveAppearance(next).catch(() => undefined);
  }

  async function signOut() {
    await auth.signOut();
    window.location.replace("/login");
  }

  return (
    <div className="app-layout">
      <aside
        id="primary-navigation"
        className={`sidebar ${menuOpen ? "open" : ""}`}
        aria-label="Primary navigation"
      >
        {/* The product's own row, then the organisation's. Two rows rather than one, so the
            tenant's identity and the product's never compete for a line: the product keeps its
            mark and its name, and the organisation keeps its logo, or a monogram of its name
            until it has uploaded one, beside its name and the switch between its two halves. */}
        <div className="sidebar-brand">
          <BrandLockup />
          {/* A narrow layout lays this panel over the header, and over the button that opened it,
              so the way out has to be inside the panel. Without it the only way to put the
              navigation away is to follow a link out of it, which is a journey nobody asked to
              make, and on a phone the workspace switch below is only reachable through here. */}
          <button
            className="icon-button nav-close"
            type="button"
            aria-label="Close navigation"
            onClick={() => setMenuOpen(false)}
          >
            <X />
          </button>
        </div>
        <div className="sidebar-tenant">
          {tenant.data?.brand.logoUrl ? (
            <img className="brand-logo" src={tenant.data.brand.logoUrl} alt="" />
          ) : (
            <span className="tenant-monogram" aria-hidden="true">
              {initials(tenantName)}
            </span>
          )}
          <span className="sidebar-tenant-text">
            <strong>{tenantName}</strong>
            <WorkspaceBadge />
          </span>
        </div>
        <nav className="sidebar-nav">
          {(["Work", "Relationships", "Operations", "Manage"] as const).map((group) => {
            const items = visible.filter((item) => item.group === group);
            if (items.length === 0) return null;
            return (
              <div className="sidebar-nav-group" key={group}>
                <span className="sidebar-nav-heading">{group}</span>
                {items.map((item) => {
                  const Icon = item.icon;
                  return (
                    <NavLink
                      key={item.to}
                      to={item.to}
                      end={item.to === "/"}
                      onClick={() => setMenuOpen(false)}
                    >
                      <Icon size={19} aria-hidden="true" />
                      {item.label}
                    </NavLink>
                  );
                })}
              </div>
            );
          })}
        </nav>
        <div className="sidebar-footer">
          {/* The person is the way to their profile page, which holds everything about the
              account: details, appearance, security. So the navigation above stays for the
              organisation, and this corner answers for the individual. */}
          <Link
            className="profile-chip"
            to="/profile"
            aria-label="Your profile"
            onClick={() => setMenuOpen(false)}
          >
            {auth.session?.user.avatarUrl ? (
              <img className="profile-avatar" src={auth.session.user.avatarUrl} alt="" />
            ) : (
              <span className="profile-avatar" aria-hidden="true">
                {initials(auth.session?.user.displayName ?? "")}
              </span>
            )}
            <span className="profile-chip-text">
              <strong>{auth.session?.user.displayName}</strong>
              <small>
                {membership
                  ? authorityLabel(membership.eventRole, membership.bookingRole)
                  : titleCase(platformRole)}
              </small>
            </span>
          </Link>
          <div className="sidebar-footer-actions">
            <fieldset className="theme-toggle" aria-label="Appearance">
              {THEMES.map((option) => {
                const Icon = themeIcons[option.id];
                const active = theme.theme === option.id;
                return (
                  <button
                    key={option.id}
                    type="button"
                    className={active ? "theme-toggle-option active" : "theme-toggle-option"}
                    aria-pressed={active}
                    aria-label={option.label}
                    title={option.label}
                    onClick={() => changeAppearance({ theme: option.id })}
                  >
                    <Icon size={15} aria-hidden="true" />
                  </button>
                );
              })}
            </fieldset>
            <button type="button" className="button ghost small" onClick={signOut}>
              Sign out
            </button>
          </div>
          <nav className="sidebar-legal" aria-label="Legal">
            <Link to="/legal/privacy">Privacy</Link>
            <Link to="/legal/terms">Terms</Link>
            <Link to="/legal/vulnerability-disclosure">Security</Link>
          </nav>
        </div>
      </aside>
      {/* Tapping the ground behind an overlay is how an overlay is put away, so the ground does
          exactly that. It is decoration for anybody reading the page rather than pointing at it:
          the close button inside the panel is the one that carries a name. */}
      {menuOpen ? (
        <button
          className="nav-scrim"
          type="button"
          tabIndex={-1}
          aria-hidden="true"
          onClick={() => setMenuOpen(false)}
        />
      ) : null}
      <div className="main-column" ref={columnRef}>
        <header className="topbar">
          {/* This opens only. While the navigation is open the panel covers this button, so the
              control that closes it lives in the panel instead, and the name here stays still:
              a name that changed with the state would be describing a button nobody can reach. */}
          <button
            className="icon-button mobile-menu"
            type="button"
            aria-controls="primary-navigation"
            aria-expanded={menuOpen}
            aria-label="Open navigation"
            onClick={() => setMenuOpen(true)}
          >
            <Menu />
          </button>
          <div className="topbar-context">
            <strong>{membership?.tenantName ?? `${PRODUCT_NAME} platform`}</strong>
            <VenueScopeBadge />
          </div>
          {auth.session && auth.session.memberships.length > 1 ? (
            <button
              className="button secondary small"
              type="button"
              onClick={() => navigate("/select-tenant")}
            >
              Switch
            </button>
          ) : null}
        </header>
        <main className="main-content" ref={contentRef} tabIndex={-1}>
          {/* The boundary sits here, inside the shell, rather than around it. A page that throws
              replaces this pane only: the sidebar, the topbar and the workspace switch stay on
              screen, so there is always a way out of a broken screen. Wrapping the shell instead
              would take the navigation down with the page, which is the failure this fixes. */}
          {/* Above the boundary rather than inside it, so a page that throws does not take the
              explanation of why the workspace is frozen down with it. */}
          <AccessBanner />
          <RouteErrorBoundary>
            <Outlet />
          </RouteErrorBoundary>
        </main>
      </div>
    </div>
  );
}
